import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { Button } from '~/ui/button'
import { Input } from '~/ui/input'
import { Label } from '~/ui/label'
import { Textarea } from '~/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/ui/select'
import { Switch } from '~/ui/switch'
import { Badge } from '~/ui/badge'
import { ArrowLeft, MessageSquare, Save, RotateCcw, Clock, Volume2, VolumeX } from 'lucide-react'
import { voiceMessagesPath } from '~/common/paths'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { AudioPlayer } from './components/audio-player'
import { PreviewAudioPlayer } from './components/preview-audio-player'
import { Slider } from '~/ui/slider'
import invariant from 'tiny-invariant'

const VOICE_MESSAGE_QUERY = gql(`
  query VoiceMessage($id: ID!) {
    voiceMessage(id: $id) {
      id
      title
      text
      ttsStatus
      ttsErrorMessage
      audioUrl
      durationSeconds
      enabled
      ttsModelId
      ttsVoiceId
      ttsSpeed
      ttsVolume
      ttsEmotion
      createdAt
      updatedAt
      durationFormatted
      displayName
      readyForPlayback
    }
  }
`)

const UPDATE_VOICE_MESSAGE_MUTATION = gql(`
  mutation UpdateVoiceMessage($input: UpdateVoiceMessageInput!) {
    updateVoiceMessage(input: $input) {
      voiceMessage {
        id
        text
        ttsStatus
        enabled
        ttsModelId
        ttsVoiceId
        ttsSpeed
        ttsVolume
        ttsEmotion
      }
      errors
    }
  }
`)

const REGENERATE_TTS_MUTATION = gql(`
  mutation RegenerateTts($input: RegenerateVoiceMessageTtsInput!) {
    regenerateVoiceMessageTts(input: $input) {
      voiceMessage {
        id
        ttsStatus
      }
      errors
    }
  }
`)

const CARTESIA_VOICE_OPTIONS_QUERY = gql(`
  query CartesiaVoiceOptionsForEdit {
    cartesiaVoiceOptions {
      defaultVoiceId
      defaultModelId
      defaultSpeed
      defaultVolume
      speedMin
      speedMax
      volumeMin
      volumeMax
      emotions
      recommendedVoiceIds
      voices {
        id
        voiceId
        name
        language
        description
        emotive
        recommended
        tags
      }
    }
  }
`)

const PREVIEW_CARTESIA_VOICE_MUTATION = gql(`
  mutation PreviewCartesiaVoice($input: PreviewCartesiaVoiceInput!) {
    previewCartesiaVoice(input: $input) {
      audioBase64
      contentType
      errors
    }
  }
`)

const DEFAULT_EMOTION = 'none'
const EMOTION_NONE_LABEL = 'None (no emotion control)'

interface FormData {
  title: string
  text: string
  enabled: boolean
  ttsVoiceId: string
  ttsSpeed: number
  ttsVolume: number
  ttsEmotion: string
  ttsModelId: string
}

export const EditVoiceMessageScreen = () => {
  const navigate = useNavigate()
  const { voiceMessageId } = useParams()
  invariant(voiceMessageId)

  const [formData, setFormData] = useState<FormData>({
    title: '',
    text: '',
    enabled: true,
    ttsVoiceId: '',
    ttsSpeed: 1,
    ttsVolume: 1,
    ttsEmotion: DEFAULT_EMOTION,
    ttsModelId: 'sonic-3',
  })
  const [errors, setErrors] = useState<string[]>([])
  const [hasTextChanged, setHasTextChanged] = useState(false)
  const [hasVoiceSettingsChanged, setHasVoiceSettingsChanged] = useState(false)
  const [, setPreviewError] = useState<string | null>(null)
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null)
  const previewAudioUrlRef = useRef<string | null>(null)

  const {
    data: voiceMessageData,
    loading: voiceMessageLoading,
    refetch,
  } = useQuery(VOICE_MESSAGE_QUERY, {
    variables: { id: voiceMessageId },
    pollInterval: 5000, // Poll to update TTS status
  })

  const [updateVoiceMessage, { loading: saving }] = useSafeMutation(UPDATE_VOICE_MESSAGE_MUTATION)
  const [regenerateTts, { loading: regenerating }] = useSafeMutation(REGENERATE_TTS_MUTATION)
  const { data: cartesiaOptionsData, loading: cartesiaOptionsLoading } = useQuery(
    CARTESIA_VOICE_OPTIONS_QUERY
  )
  const [previewCartesiaVoice, { loading: previewing }] = useSafeMutation(
    PREVIEW_CARTESIA_VOICE_MUTATION
  )

  const cartesiaOptions = cartesiaOptionsData?.cartesiaVoiceOptions

  const defaultEmotion = DEFAULT_EMOTION

  // Only set defaults on initial load when cartesiaOptions first becomes available
  // Don't reset user-entered values when cartesiaOptions changes
  const hasInitializedDefaults = useRef(false)
  useEffect(() => {
    if (!cartesiaOptions || hasInitializedDefaults.current) return

    setFormData((prev) => {
      // Only set defaults if fields are empty/unset (initial state)
      const updates: Partial<FormData> = {}

      if (!prev.ttsVoiceId) {
        updates.ttsVoiceId = cartesiaOptions.defaultVoiceId
      }
      if (!prev.ttsModelId) {
        updates.ttsModelId = cartesiaOptions.defaultModelId
      }
      if (prev.ttsSpeed === 1) {
        // Only reset speed if it's still at the initial default (1)
        updates.ttsSpeed = cartesiaOptions.defaultSpeed
      }
      if (prev.ttsVolume === 1) {
        // Only reset volume if it's still at the initial default (1)
        updates.ttsVolume = cartesiaOptions.defaultVolume
      }
      if (!prev.ttsEmotion || prev.ttsEmotion.length === 0) {
        updates.ttsEmotion = defaultEmotion
      }

      // Mark as initialized after first run
      hasInitializedDefaults.current = true

      // Only update if there are changes
      if (Object.keys(updates).length === 0) {
        return prev
      }

      return { ...prev, ...updates }
    })
  }, [cartesiaOptions, defaultEmotion])

  // Clean up blob URL on unmount only
  useEffect(() => {
    return () => {
      if (previewAudioUrlRef.current) {
        URL.revokeObjectURL(previewAudioUrlRef.current)
        previewAudioUrlRef.current = null
      }
    }
  }, [])

  const availableVoices = useMemo(() => {
    const allVoices = cartesiaOptions?.voices ?? []
    // Filter to only emotive voices when an emotion is selected
    if (formData.ttsEmotion && formData.ttsEmotion !== 'none') {
      return allVoices.filter((voice: any) => voice.emotive === true)
    }
    return allVoices
  }, [cartesiaOptions?.voices, formData.ttsEmotion])

  // Initialize form data when voice message loads
  useEffect(() => {
    if (voiceMessageData?.voiceMessage) {
      const vm = voiceMessageData.voiceMessage
      setFormData({
        title: vm.title ?? '',
        text: vm.text,
        enabled: vm.enabled,
        ttsVoiceId: vm.ttsVoiceId,
        ttsSpeed: vm.ttsSpeed ?? cartesiaOptions?.defaultSpeed ?? 1,
        ttsVolume: vm.ttsVolume ?? cartesiaOptions?.defaultVolume ?? 1,
        ttsEmotion: vm.ttsEmotion ?? defaultEmotion,
        ttsModelId: vm.ttsModelId ?? cartesiaOptions?.defaultModelId ?? 'sonic-3',
      })
      setHasTextChanged(false)
      setHasVoiceSettingsChanged(false)
      if (previewAudioUrlRef.current) {
        URL.revokeObjectURL(previewAudioUrlRef.current)
        previewAudioUrlRef.current = null
        setPreviewAudioUrl(null)
      }
    }
  }, [voiceMessageData, cartesiaOptions, defaultEmotion])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors([])

    // Client-side validation
    const newErrors: string[] = []
    if (!formData.text.trim()) newErrors.push('Message text is required')
    if (formData.text.length > 5000) newErrors.push('Message text must be 5000 characters or less')

    if (newErrors.length > 0) {
      setErrors(newErrors)
      return
    }

    const result = await updateVoiceMessage({
      variables: {
        input: {
          id: voiceMessageId,
          input: {
            title: formData.title.trim(),
            text: formData.text.trim(),
            enabled: formData.enabled,
            ttsVoiceId: formData.ttsVoiceId,
            ttsSpeed: formData.ttsSpeed,
            ttsVolume: formData.ttsVolume,
            ttsEmotion: formData.ttsEmotion,
            ttsModelId: formData.ttsModelId,
          },
        },
      },
    })

    if (result.data?.updateVoiceMessage?.voiceMessage) {
      navigate(voiceMessagesPath({}))
    } else if (result.data?.updateVoiceMessage?.errors) {
      setErrors(result.data.updateVoiceMessage.errors)
    }
  }

  const handleInputChange = (field: keyof FormData) => (value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }

      if (voiceMessageData?.voiceMessage) {
        const vm = voiceMessageData.voiceMessage
        if (field === 'text') {
          setHasTextChanged(next.text !== vm.text)
        }

        if (['ttsVoiceId', 'ttsSpeed', 'ttsVolume', 'ttsEmotion', 'ttsModelId'].includes(field)) {
          const speedChanged = Math.abs((next.ttsSpeed ?? 1) - (vm.ttsSpeed ?? 1)) > 0.001
          const volumeChanged = Math.abs((next.ttsVolume ?? 1) - (vm.ttsVolume ?? 1)) > 0.001
          const emotionChanged = next.ttsEmotion !== (vm.ttsEmotion ?? DEFAULT_EMOTION)
          const voiceChanged = next.ttsVoiceId !== vm.ttsVoiceId
          const modelChanged = next.ttsModelId !== (vm.ttsModelId ?? 'sonic-3')

          setHasVoiceSettingsChanged(
            voiceChanged || speedChanged || volumeChanged || emotionChanged || modelChanged
          )
        }
      }

      return next
    })

    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([])
    }
  }

  const handleSliderChange = (field: 'ttsSpeed' | 'ttsVolume') => (value: number[]) => {
    const numericValue = value[0]
    handleInputChange(field)(numericValue)
  }

  const handleRegenerateTts = async () => {
    await regenerateTts({
      variables: { input: { id: voiceMessageId } },
    })
    refetch()
  }

  const handlePreview = async () => {
    setPreviewError(null)

    if (!formData.text.trim()) {
      setPreviewError('Enter message text to preview the voice')
      return
    }

    // Revoke previous URL if it exists
    if (previewAudioUrlRef.current) {
      URL.revokeObjectURL(previewAudioUrlRef.current)
      previewAudioUrlRef.current = null
    }
    setPreviewAudioUrl(null)

    try {
      // Build input object, only including voice_id if it's set
      const previewInput: any = {
        text: formData.text.trim(),
        ttsSpeed: formData.ttsSpeed,
        ttsVolume: formData.ttsVolume,
        ttsEmotion: formData.ttsEmotion === 'none' ? null : formData.ttsEmotion,
        ttsModelId: formData.ttsModelId,
      }

      // Only include ttsVoiceId if it's actually set (not empty string)
      if (formData.ttsVoiceId && formData.ttsVoiceId.trim() !== '') {
        previewInput.ttsVoiceId = formData.ttsVoiceId
      }

      const result = await previewCartesiaVoice({
        variables: {
          input: {
            input: previewInput,
          },
        },
      })

      const previewResponse = result.data?.previewCartesiaVoice

      if (!previewResponse) {
        console.error('No preview response received')
        setPreviewError('Failed to generate preview - no response from server')
        return
      }

      if (previewResponse.errors && previewResponse.errors.length > 0) {
        console.error('Preview errors:', previewResponse.errors)
        setPreviewError(previewResponse.errors[0])
        return
      }

      const audioBase64 = previewResponse.audioBase64
      if (!audioBase64) {
        console.error('No audio data in preview response')
        setPreviewError('No audio data received from server')
        return
      }

      try {
        const byteCharacters = atob(audioBase64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], {
          type: previewResponse.contentType ?? 'audio/mpeg',
        })

        if (blob.size === 0) {
          console.error('Created blob has zero size')
          setPreviewError('Generated audio file is empty')
          return
        }

        const url = URL.createObjectURL(blob)
        console.log('Preview audio created:', { size: blob.size, type: blob.type, url })
        previewAudioUrlRef.current = url
        setPreviewAudioUrl(url)
        // PreviewAudioPlayer will handle auto-play
      } catch (decodeError) {
        console.error('Error decoding base64 audio:', decodeError)
        setPreviewError('Failed to decode audio data')
      }
    } catch (error) {
      console.error('Preview generation error:', error)
      setPreviewError('Failed to generate preview - please try again')
    }
  }

  const getTtsStatusBadge = (status: string, errorMessage?: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>
      case 'processing':
        return <Badge variant="secondary">Processing...</Badge>
      case 'completed':
        return <Badge variant="default">Ready</Badge>
      case 'failed':
        return (
          <Badge variant="destructive" title={errorMessage}>
            Failed
          </Badge>
        )
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  if (voiceMessageLoading) {
    return (
      <div className="flex h-screen flex-col">
        <div className="flex-shrink-0 border-b p-6">
          <div className="mx-auto max-w-2xl">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate(voiceMessagesPath({}))}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Voice Messages
              </Button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-2xl">
            <Card className="animate-pulse">
              <CardHeader>
                <div className="mb-2 h-6 w-3/4 rounded bg-gray-200"></div>
                <div className="h-4 w-1/2 rounded bg-gray-200"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="h-4 w-full rounded bg-gray-200"></div>
                  <div className="h-20 w-full rounded bg-gray-200"></div>
                  <div className="h-4 w-1/3 rounded bg-gray-200"></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (!voiceMessageData?.voiceMessage) {
    return (
      <div className="flex h-screen flex-col">
        <div className="flex-shrink-0 border-b p-6">
          <div className="mx-auto max-w-2xl">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate(voiceMessagesPath({}))}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Voice Messages
              </Button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="py-12 text-center">
            <p className="text-red-600">Voice message not found</p>
            <Button onClick={() => navigate(voiceMessagesPath({}))} className="mt-4">
              Back to Voice Messages
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const voiceMessage = voiceMessageData.voiceMessage

  return (
    <div className="flex h-screen flex-col">
      <div className="flex-shrink-0 border-b p-6">
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(voiceMessagesPath({}))}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Voice Messages
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-2xl">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <MessageSquare className="h-6 w-6" />
                <div className="flex-1">
                  <CardTitle>Edit Voice Message</CardTitle>
                  <CardDescription>Update your text-to-speech announcement</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {getTtsStatusBadge(
                    voiceMessage.ttsStatus,
                    voiceMessage.ttsErrorMessage ?? undefined
                  )}
                  {voiceMessage.enabled ? (
                    <Volume2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <VolumeX className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {errors.length > 0 && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4">
                    <div className="text-sm text-red-700">
                      <div className="mb-2 font-medium">Please fix the following errors:</div>
                      <ul className="list-inside list-disc space-y-1">
                        {errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* TTS Status Info */}
                <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">Text-to-Speech Status</div>
                      <div className="text-muted-foreground text-sm">
                        {voiceMessage.ttsStatus === 'completed' &&
                          voiceMessage.durationFormatted && (
                            <>Duration: {voiceMessage.durationFormatted}</>
                          )}
                        {voiceMessage.ttsStatus === 'failed' && voiceMessage.ttsErrorMessage && (
                          <>Error: {voiceMessage.ttsErrorMessage}</>
                        )}
                        {voiceMessage.ttsStatus === 'pending' && <>Waiting for TTS generation...</>}
                        {voiceMessage.ttsStatus === 'processing' && <>Generating speech audio...</>}
                      </div>
                    </div>
                    {(voiceMessage.ttsStatus === 'failed' ||
                      voiceMessage.ttsStatus === 'completed') && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRegenerateTts}
                        disabled={regenerating}
                      >
                        <RotateCcw className="mr-1 h-4 w-4" />
                        {regenerating ? 'Regenerating...' : 'Regenerate TTS'}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title')(e.target.value)}
                    placeholder="Give your voice message a name..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="text">Message Text *</Label>
                  <Textarea
                    id="text"
                    value={formData.text}
                    onChange={(e) => handleInputChange('text')(e.target.value)}
                    placeholder="Enter the text that will be converted to speech..."
                    rows={6}
                    className="resize-none"
                  />
                  <div className="text-muted-foreground flex justify-between text-sm">
                    <span>
                      The text that will be converted to speech using AI
                      {(hasTextChanged || hasVoiceSettingsChanged) &&
                        voiceMessage.ttsStatus === 'completed' && (
                          <span className="ml-2 text-amber-600">(Will be regenerated on save)</span>
                        )}
                    </span>
                    <span className={formData.text.length > 5000 ? 'text-red-600' : ''}>
                      {formData.text.length}/5000 characters
                    </span>
                  </div>
                </div>

                <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-medium">Voice Settings</div>
                      <p className="text-muted-foreground text-sm">
                        Fine-tune the delivery and preview your announcement instantly.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handlePreview}
                      disabled={previewing || cartesiaOptionsLoading || !formData.ttsVoiceId}
                    >
                      {previewing ? 'Generating preview...' : 'Preview voice'}
                    </Button>
                  </div>

                  <div className="grid w-full gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="voice">Voice</Label>
                      <Select
                        value={formData.ttsVoiceId}
                        onValueChange={handleInputChange('ttsVoiceId')}
                        disabled={cartesiaOptionsLoading || availableVoices.length === 0}
                      >
                        <SelectTrigger className="w-full" size="default">
                          <SelectValue
                            placeholder={
                              cartesiaOptionsLoading ? 'Loading voices...' : 'Select a voice'
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {availableVoices.map((voice: any) => {
                            const voiceId = voice.voiceId ?? voice.id
                            const isEmotive = voice.emotive ?? false
                            const gender = voice.gender
                              ? voice.gender.charAt(0).toUpperCase() + voice.gender.slice(1)
                              : ''
                            const emotiveTags = voice.tags
                              ? voice.tags.split(', ').filter((t: string) => t === 'entertainment')
                              : []

                            return (
                              <SelectItem key={voiceId} value={voiceId}>
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {voice.name ?? 'Unnamed voice'}
                                    </span>
                                    {gender && (
                                      <span className="text-muted-foreground text-xs">•</span>
                                    )}
                                    {gender && (
                                      <span className="text-muted-foreground text-xs">
                                        {gender}
                                      </span>
                                    )}
                                    {isEmotive && (
                                      <Badge
                                        variant="secondary"
                                        className="text-[9px] tracking-wide uppercase"
                                      >
                                        Emotive
                                      </Badge>
                                    )}
                                    {emotiveTags.map((tag: string) => (
                                      <Badge
                                        key={tag}
                                        variant="secondary"
                                        className="text-[9px] tracking-wide uppercase"
                                      >
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                  {voice.description && (
                                    <span className="text-muted-foreground text-xs">
                                      {voice.description}
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            )
                          })}
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-xs">
                        {availableVoices.length} voices available
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Emotion</Label>
                      <Select
                        value={formData.ttsEmotion}
                        onValueChange={handleInputChange('ttsEmotion')}
                        disabled={cartesiaOptionsLoading}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select emotion" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem key="none" value="none">
                            {EMOTION_NONE_LABEL}
                          </SelectItem>
                          {(cartesiaOptions?.emotions ?? []).map((emotion) => (
                            <SelectItem key={emotion} value={emotion}>
                              {emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-xs">
                        Choose an emotional tone or select "None" for neutral delivery
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Speed ({formData.ttsSpeed.toFixed(2)}×)</Label>
                      <Slider
                        value={[formData.ttsSpeed]}
                        min={cartesiaOptions?.speedMin ?? 0.6}
                        max={cartesiaOptions?.speedMax ?? 1.5}
                        step={0.05}
                        onValueChange={handleSliderChange('ttsSpeed')}
                      />
                      <p className="text-muted-foreground text-xs">
                        Slow it down for clarity or speed it up for urgency
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Volume ({formData.ttsVolume.toFixed(2)}×)</Label>
                      <Slider
                        value={[formData.ttsVolume]}
                        min={cartesiaOptions?.volumeMin ?? 0}
                        max={cartesiaOptions?.volumeMax ?? 2}
                        step={0.05}
                        onValueChange={handleSliderChange('ttsVolume')}
                      />
                      <p className="text-muted-foreground text-xs">
                        Boost or soften the announcement volume relative to the default
                      </p>
                    </div>
                  </div>

                  {previewAudioUrl && (
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                      <PreviewAudioPlayer
                        audioUrl={previewAudioUrl}
                        onFinished={() => {
                          if (previewAudioUrlRef.current) {
                            URL.revokeObjectURL(previewAudioUrlRef.current)
                            previewAudioUrlRef.current = null
                          }
                          setPreviewAudioUrl(null)
                        }}
                      />
                    </div>
                  )}

                  {hasVoiceSettingsChanged && voiceMessage.ttsStatus !== 'pending' && (
                    <p className="text-xs text-amber-600">
                      Voice settings changed — the audio will be regenerated on save.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <Switch
                      id="enabled"
                      checked={formData.enabled}
                      onCheckedChange={handleInputChange('enabled')}
                    />
                    <Label htmlFor="enabled" className="text-sm font-medium">
                      Enable voice message
                    </Label>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Disabled voice messages will not be played even if scheduled
                  </p>
                </div>

                {voiceMessage.audioUrl && voiceMessage.ttsStatus === 'completed' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">Audio Ready</span>
                      <span>•</span>
                      <span>Duration: {voiceMessage.durationFormatted}</span>
                      <span>•</span>
                      <span>Created {new Date(voiceMessage.createdAt).toLocaleDateString()}</span>
                    </div>

                    <AudioPlayer
                      audioUrl={voiceMessage.audioUrl}
                      title={voiceMessage.displayName}
                    />
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 border-t pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(voiceMessagesPath({}))}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
